import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [step, setStep] = useState('phone'); // phone | pin | set-pin | confirm-pin
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+92');
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [userName, setUserName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const pinRefs = [useRef(), useRef(), useRef(), useRef()];
  const confirmRefs = [useRef(), useRef(), useRef(), useRef()];
  const { login } = useAuth();
  const navigate = useNavigate();

  const fullPhone = `${countryCode}${phone}`;

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await API.post('/auth/check', { phone: fullPhone });
      setUserName(res.data.name);
      if (res.data.firstLogin) {
        setStep('set-pin');
      } else {
        setStep('pin');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error checking phone number');
    } finally {
      setLoading(false);
    }
  };

  const handlePinInput = (index, value, arr, setArr, refs, nextAction) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = [...arr];
    newPin[index] = value.slice(-1);
    setArr(newPin);
    if (value && index < 3) refs[index + 1].current?.focus();
    if (value && index === 3) {
      const complete = [...newPin];
      if (complete.every(d => d)) nextAction(complete.join(''));
    }
  };

  const handlePinKeyDown = (index, e, refs) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) refs[index - 1].current?.focus();
  };

  const submitLogin = async (pinStr) => {
    setError('');
    setLoading(true);
    try {
      const res = await API.post('/auth/login', { phone: fullPhone, pin: pinStr });
      login(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid PIN');
      setPin(['', '', '', '']);
      pinRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSetPin = (pinStr) => {
    setStep('confirm-pin');
    setTimeout(() => confirmRefs[0].current?.focus(), 100);
  };

  const handleConfirmPin = async (confirmStr) => {
    if (pin.join('') !== confirmStr) {
      setError('PINs do not match. Try again.');
      setConfirmPin(['', '', '', '']);
      setPin(['', '', '', '']);
      setStep('set-pin');
      setTimeout(() => pinRefs[0].current?.focus(), 100);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await API.post('/auth/set-pin', { phone: fullPhone, pin: pin.join('') });
      login(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Error setting PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', background: 'var(--bg)' }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎾</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 32, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-1px' }}>Pedal Toast</div>
          <div style={{ color: 'var(--text2)', fontSize: 14, marginTop: 6 }}>Your padel squad, organised.</div>
        </div>

        {error && <div className="error-msg">{error}</div>}

        {step === 'phone' && (
          <form onSubmit={handlePhoneSubmit} className="fade-in">
            <div className="form-label">Phone Number</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              <select
                value={countryCode}
                onChange={e => setCountryCode(e.target.value)}
                className="form-input"
                style={{ width: 90, flexShrink: 0 }}
              >
                <option value="+92">🇵🇰 +92</option>
                <option value="+971">🇦🇪 +971</option>
                <option value="+1">🇺🇸 +1</option>
                <option value="+44">🇬🇧 +44</option>
              </select>
              <input
                type="tel"
                className="form-input"
                placeholder="3001234567"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={!phone || loading}>
              {loading ? 'Checking...' : 'Continue →'}
            </button>
          </form>
        )}

        {step === 'pin' && (
          <div className="fade-in">
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>Welcome back, {userName}! 👋</div>
              <div style={{ color: 'var(--text2)', fontSize: 14, marginTop: 4 }}>Enter your 4-digit PIN</div>
            </div>
            <div className="pin-row">
              {pin.map((d, i) => (
                <input
                  key={i}
                  ref={pinRefs[i]}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  className="pin-digit"
                  value={d}
                  onChange={e => handlePinInput(i, e.target.value, pin, setPin, pinRefs, submitLogin)}
                  onKeyDown={e => handlePinKeyDown(i, e, pinRefs)}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            {loading && <div style={{ textAlign: 'center', color: 'var(--text2)', fontSize: 14 }}>Logging in...</div>}
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => { setStep('phone'); setPin(['','','','']); setError(''); }}>
                ← Change number
              </button>
            </div>
          </div>
        )}

        {step === 'set-pin' && (
          <div className="fade-in">
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>Hi {userName}! 🎉</div>
              <div style={{ color: 'var(--text2)', fontSize: 14, marginTop: 4 }}>Set your 4-digit PIN to get started</div>
            </div>
            <div className="pin-row">
              {pin.map((d, i) => (
                <input
                  key={i}
                  ref={pinRefs[i]}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  className="pin-digit"
                  value={d}
                  onChange={e => handlePinInput(i, e.target.value, pin, setPin, pinRefs, handleSetPin)}
                  onKeyDown={e => handlePinKeyDown(i, e, pinRefs)}
                  autoFocus={i === 0}
                />
              ))}
            </div>
          </div>
        )}

        {step === 'confirm-pin' && (
          <div className="fade-in">
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>Confirm your PIN</div>
              <div style={{ color: 'var(--text2)', fontSize: 14, marginTop: 4 }}>Enter the same PIN again</div>
            </div>
            <div className="pin-row">
              {confirmPin.map((d, i) => (
                <input
                  key={i}
                  ref={confirmRefs[i]}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  className="pin-digit"
                  value={d}
                  onChange={e => handlePinInput(i, e.target.value, confirmPin, setConfirmPin, confirmRefs, handleConfirmPin)}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            {loading && <div style={{ textAlign: 'center', color: 'var(--text2)', fontSize: 14 }}>Setting up...</div>}
          </div>
        )}
      </div>
    </div>
  );
}
